import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RestaurantRawMaterialCategoryService from '../../services/restaurant/RestaurantRawMaterialCategoryService';
import RestaurantRawMaterialService from '../../services/restaurant/RestaurantRawMaterialService';
import {
  closeRawMaterial,
  openRawMaterialCreate,
  openRawMaterialEdit,
  setRawMaterialLoading,
  setRawMaterialOptions,
  setRawMaterialPage,
  setRawMaterialSaving,
  updateRawMaterialField,
} from '../../store/slices/restaurant/restaurantRawMaterialSlice';
import { AppDispatch, RootState } from '../../store/store';
import { RestaurantPageRequest } from '../../types/restaurant/RestaurantRecipeTypes';
import {
  RestaurantRawMaterialDTO,
  RestaurantRawMaterialFilters,
  RestaurantRawMaterialForm,
} from '../../types/restaurant/RestaurantRawMaterialTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantRawMaterials = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantRawMaterial);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const requestId = useRef(0);

  const loadOptions = async () => {
    if (!branchId) return;
    try {
      const [categories, units, suppliers] = await Promise.all([
        RestaurantRawMaterialCategoryService.listFiltered(branchId, {
          page: 0,
          size: 50,
          sort: ['name,asc'],
          filters: { search: null, active: true },
        }),
        RestaurantRawMaterialService.units(),
        RestaurantRawMaterialService.suppliers(),
      ]);
      dispatch(
        setRawMaterialOptions({
          categories: categories.content,
          units,
          suppliers,
        })
      );
    } catch (error) {
      handle(error);
    }
  };

  const load = async (
    request: RestaurantPageRequest<RestaurantRawMaterialFilters>
  ) => {
    if (!branchId) return;
    const currentRequest = ++requestId.current;
    dispatch(setRawMaterialLoading(true));
    try {
      const page = await RestaurantRawMaterialService.listFiltered(
        branchId,
        request
      );
      if (currentRequest !== requestId.current) return;
      dispatch(setRawMaterialPage(page));
      return page;
    } catch (error) {
      if (currentRequest === requestId.current) handle(error);
    } finally {
      if (currentRequest === requestId.current) {
        dispatch(setRawMaterialLoading(false));
      }
    }
  };

  const edit = async (item: RestaurantRawMaterialDTO) => {
    try {
      dispatch(
        openRawMaterialEdit(
          (await RestaurantRawMaterialService.get(item.id)).data
        )
      );
    } catch (error) {
      handle(error);
    }
  };

  const save = async (
    request: RestaurantPageRequest<RestaurantRawMaterialFilters>,
    onEmptyPage: () => void
  ) => {
    if (!branchId || state.saving) return;
    dispatch(setRawMaterialSaving(true));
    try {
      const response = state.form.id
        ? await RestaurantRawMaterialService.update(state.form.id, state.form)
        : await RestaurantRawMaterialService.create(branchId, state.form);
      show(
        response.type || 'success',
        response.title || 'Materia prima guardada',
        response.message || 'El registro se guardó correctamente.'
      );
      dispatch(closeRawMaterial());
      const page = await load(request);
      if (page?.content.length === 0 && request.page > 0) onEmptyPage();
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRawMaterialSaving(false));
    }
  };

  const toggleActive = (
    item: RestaurantRawMaterialDTO,
    request: RestaurantPageRequest<RestaurantRawMaterialFilters>,
    onEmptyPage: () => void
  ) => {
    const execute = async () => {
      try {
        const response = await RestaurantRawMaterialService.updateActive(
          item.id,
          !item.active
        );
        show(
          response.type || 'success',
          response.title || 'Registro actualizado',
          response.message || 'Estado actualizado.'
        );
        const page = await load(request);
        if (page?.content.length === 0 && request.page > 0) onEmptyPage();
      } catch (error) {
        handle(error);
      }
    };
    if (!item.active) return execute();
    confirm({
      title: 'Desactivar materia prima',
      message: `¿Desea desactivar “${item.name}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: execute,
    });
  };

  const updateField = <K extends keyof RestaurantRawMaterialForm>(
    field: K,
    value: RestaurantRawMaterialForm[K]
  ) => dispatch(updateRawMaterialField({ field, value }));

  return {
    ...state,
    load,
    loadOptions,
    edit,
    save,
    toggleActive,
    updateField,
    openCreate: () => dispatch(openRawMaterialCreate()),
    closeModal: () => dispatch(closeRawMaterial()),
  };
};

export default useRestaurantRawMaterials;
